import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Password } from "@mui/icons-material";
import { setPasswordSchema } from "../../services/validator";
import useEncryption from "../../services/useEncryption";
import useToast from "../../helpers/useToast";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { setPasswordApi } from "../../api/authApi/authApi";

const SetPassword = () => {
  const [resetSuccessful, setResetSuccessful] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();

  const { encryptData } = useEncryption();
  const toastOptions = useToast();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isNewUser = searchParams.get("new") === "true";

  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const validationSchema = setPasswordSchema;

  const onSubmit = async (values) => {
    try {
      const data = encryptData(values);
      const response = await setPasswordApi(data, token);
      setResetSuccessful(true);
      toast.success(response.data.message, toastOptions);
      setTimeout(() => navigate("/"), 3000);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred",
        toastOptions
      );
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box
        sx={{
          width: "100%",
          height: "420px",
          padding: "20px",
          border: "1px solid gray",
          borderRadius: "10px",
          marginTop: "2%",
          marginBottom: "2%",
          boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ color: "#2192E5" }}
        >
          {isNewUser ? "Set Your Password" : "Reset Your Password"}
        </Typography>
        <Box display="flex" justifyContent="center" my={2}>
          <Password sx={{ fontSize: 50, color: "#F78220" }} />
        </Box>
        {resetSuccessful ? (
          <Typography
            variant="body2"
            align="center"
            sx={{ color: "#D29705", marginTop: 2 }}
          >
            Your password has been reset successfully. Redirecting to login...
          </Typography>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Box mb={2}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                autoComplete="off"
                size="small"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm Password"
                autoComplete="off"
                size="small"
                type={showConfirmPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              className="btnMain"
              fullWidth
              sx={{ mt: 2 }}
            >
              {isNewUser ? "Set Password" : "Reset Password"}
            </Button>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Link to="/" className="sideLink">
                Go to Login
              </Link>
            </Box>
          </form>
        )}
      </Box>
    </Container>
  );
};

export default SetPassword;
